import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pin, X as XIcon, Sparkles, Dumbbell, Calendar, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders the user's pinned AI Coach plan + pinned Workout on their Profile.
 * Each pin slot can be unpinned independently.
 */
export default function PinnedPlans() {
  const [pinned, setPinned] = useState({ coach: null, workout: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profile/pinned`);
      setPinned(data || { coach: null, workout: null });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const unpin = async (kind) => {
    if (!window.confirm(`Unpin your ${kind === "coach" ? "AI Coach plan" : "workout"}?`)) return;
    try {
      await axios.delete(`${API}/profile/pin/${kind}`);
      toast.success("Unpinned.");
      load();
    } catch (e) {
      toast.error("Unpin failed.");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-6 text-neutral-500 text-sm">
        <Loader2 className="animate-spin inline mr-2" size={14} />
        Loading pinned plans…
      </div>
    );
  }

  if (!pinned.coach && !pinned.workout) {
    return (
      <div
        data-testid="pinned-plans-empty"
        className="border border-dashed border-zinc-800 p-6 text-center"
      >
        <Pin size={24} className="mx-auto text-neutral-700 mb-2" />
        <p className="text-sm text-neutral-500">
          Nothing pinned yet. Generate an AI Coach plan or open the Workout of the Week —
          you'll see a <strong className="text-[#D4AF37]">Pin to Profile</strong> button.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="pinned-plans" className="space-y-4">
      {pinned.coach && <CoachPinCard pin={pinned.coach} onUnpin={() => unpin("coach")} />}
      {pinned.workout && <WorkoutPinCard pin={pinned.workout} onUnpin={() => unpin("workout")} />}
    </div>
  );
}

function CoachPinCard({ pin, onUnpin }) {
  const d = pin?.data || {};
  const [expanded, setExpanded] = useState(false);
  const plan = d.plan || "";
  const preview = plan.split("\n").slice(0, 6).join("\n");

  return (
    <div
      data-testid="pinned-coach"
      className="bg-[#0F0F0F] border-2 border-[#D4AF37] p-5 relative"
    >
      <button
        data-testid="pinned-coach-unpin"
        onClick={onUnpin}
        className="absolute top-3 right-3 text-neutral-500 hover:text-[#DC2626]"
        title="Unpin"
      >
        <XIcon size={14} />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          AI Coach · Pinned
        </span>
      </div>
      <h4 className="font-display text-2xl text-white mb-2">
        {d.goal ? `Plan: ${d.goal}` : "My Coach Plan"}
        {d.level && <span className="text-sm text-neutral-500 font-sans ml-2">· {d.level}</span>}
      </h4>
      <div
        className="ai-prose text-sm prose-invert"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(expanded ? plan : preview) }}
      />
      {plan.length > preview.length && (
        <button
          data-testid="pinned-coach-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-xs uppercase tracking-widest text-[#D4AF37] hover:text-white border border-[#D4AF37]/30 hover:border-[#D4AF37] px-3 py-1.5"
        >
          {expanded ? "Show less" : "Read full plan"}
        </button>
      )}
      {pin.pinned_at && (
        <p className="mt-3 text-[10px] uppercase tracking-widest text-neutral-600">
          Pinned {new Date(pin.pinned_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

function WorkoutPinCard({ pin, onUnpin }) {
  const d = pin?.data || {};
  return (
    <div
      data-testid="pinned-workout"
      className="bg-[#0F0F0F] border-2 border-[#DC2626] p-5 relative"
    >
      <button
        data-testid="pinned-workout-unpin"
        onClick={onUnpin}
        className="absolute top-3 right-3 text-neutral-500 hover:text-[#DC2626]"
        title="Unpin"
      >
        <XIcon size={14} />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <Dumbbell size={14} className="text-[#DC2626]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#DC2626] font-bold">
          Workout · Pinned
        </span>
      </div>
      <h4 className="font-display text-2xl text-white">{d.title || "My Workout"}</h4>
      {d.tagline && (
        <p className="text-red-400 italic text-sm mt-1">"{d.tagline}"</p>
      )}
      <div className="flex flex-wrap gap-2 mt-3 text-[10px] uppercase tracking-widest text-neutral-400">
        {d.focus && <span className="border border-zinc-700 px-2 py-1">{d.focus}</span>}
        {d.level && <span className="border border-zinc-700 px-2 py-1">{d.level}</span>}
        {d.days_per_week && (
          <span className="border border-zinc-700 px-2 py-1 inline-flex items-center gap-1">
            <Calendar size={10} /> {d.days_per_week} d/wk
          </span>
        )}
        {d.duration_min && (
          <span className="border border-zinc-700 px-2 py-1">{d.duration_min}m</span>
        )}
      </div>
      {(d.days || []).length > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-xs uppercase tracking-widest text-[#D4AF37] hover:text-white list-none">
            ▸ Show {d.days.length}-day schedule
          </summary>
          <div className="mt-2 grid gap-2">
            {d.days.map((day, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-zinc-800 p-2 text-xs">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                  Day {i + 1}{day.title ? ` · ${day.title}` : ""}
                </div>
                {(day.exercises || []).slice(0, 6).map((ex, j) => (
                  <div key={j} className="text-neutral-300 leading-snug">
                    • {ex.name} {ex.sets ? `· ${ex.sets}×${ex.reps || "?"}` : ""}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </details>
      )}
      {pin.pinned_at && (
        <p className="mt-3 text-[10px] uppercase tracking-widest text-neutral-600">
          Pinned {new Date(pin.pinned_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

// Minimal Markdown renderer — same shape as AiCoach.jsx but trimmed.
function renderMarkdown(md) {
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>(\n|$))+/g, (m) => `<ul>${m.replace(/\n/g, "")}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, "<br/>");
  return `<p>${html}</p>`;
}
